import type { HTMLAttributes } from 'react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from 'styled-components';
import { isEqual } from 'es-toolkit';
import { uniqueId } from 'es-toolkit/compat';
import maplibregl from 'maplibre-gl';
import { createPortal } from 'react-dom';
import { useMapInit } from './map-init';
import type {
  FlyToOptions,
  MapAPI,
  MapPositionParams,
  MapStyle,
  MarkerCallback,
  PointType
} from './map-types';
import { Marker } from './marker';
import { UserLocation } from './user-location';
import { calculateBounds } from './utils/calculate-bounds';
import { createPointsGeoJson } from './utils/create-geojson';
import { getStyleURL } from './utils/get-style-url';
import { isPointInBounds } from './utils/is-point-in-bounds';
import { setMapPosition } from './utils/set-map-position';

export type MapProps = HTMLAttributes<HTMLDivElement> & {
  points?: PointType[];
  center?: 'auto' | { lng: number; lat: number };
  zoom?: 'auto' | number;
  width?: string;
  height?: string;
  mapStyle?: MapStyle;
  locale?: string;

  markerColor?: string;
  markerIconUrl?: string;

  flyToOptions?: FlyToOptions;

  staticMode?: boolean;

  widgetToken?: string;
};

export const MapComponent = forwardRef<MapAPI, MapProps>(function MapComponent(
  {
    points = [],
    center = 'auto',
    zoom = 'auto',
    height = '100%',
    width = '100%',
    mapStyle = 'classic',
    markerColor = '#000',
    markerIconUrl,
    flyToOptions = {},
    staticMode = false,
    locale,
    widgetToken,
    ...forwardingProps
  },
  forwardingAPIRef
) {
  const containerId = useMemo(() => uniqueId('elfsight_map_'), []);

  const [rerenderMarkers, triggerRerenderMarkers] = useState({});
  const pointsGeoJson = createPointsGeoJson(points);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // При инициализации грузим воду с большим зумом, чтобы при определении корректной позиции
  // было меньше визуального шума + экономия тайлов
  const mapInitOptions = {
    center: { lng: 49, lat: -29 },
    zoom: 20
  };

  const map = useMapInit({
    ...mapInitOptions,
    staticMode,
    containerRef,
    mapStyle,
    points,
    center,
    zoom,
    locale,
    widgetToken,
    flyToOptions
  });

  const onMarkerHoverCallbacksRef = useRef<Record<string, MarkerCallback>>({});
  const onMarkerClickCallbacksRef = useRef<Record<string, MarkerCallback>>({});

  useEffect(() => {
    const markers = map?._container.querySelectorAll('.maplibregl-marker');
    markers?.forEach((marker) => {
      marker.remove();
    });

    pointsGeoJson.features.forEach((point) => {
      if (!containerRef.current) return;

      const uniqueId = `${containerRef.current.id}_${point.properties.id}`;

      const markerEl = document.createElement('div');
      markerEl.classList.add(uniqueId);
      markerEl.role = 'button';

      const markerClickHandler = () => {
        if (onMarkerClickCallbacksRef.current) {
          Object.values(onMarkerClickCallbacksRef.current).forEach((callback) =>
            callback(point.properties.id)
          );
        }
      };

      const mouseOverHandler = () => {
        if (onMarkerHoverCallbacksRef.current) {
          Object.values(onMarkerHoverCallbacksRef.current).forEach((callback) =>
            callback(point.properties.id)
          );
        }
      };

      const mouseLeaveHandler = () => {
        if (onMarkerHoverCallbacksRef.current) {
          Object.values(onMarkerHoverCallbacksRef.current).forEach((callback) =>
            callback(undefined)
          );
        }
      };

      markerEl.addEventListener('click', markerClickHandler);
      markerEl.addEventListener('mouseover', mouseOverHandler);
      markerEl.addEventListener('mouseleave', mouseLeaveHandler);

      const marker = new maplibregl.Marker({ element: markerEl }).setLngLat(
        point.geometry.coordinates
      );

      if (map) {
        marker.addTo(map);
      }

      return () => {
        markerEl.removeEventListener('click', markerClickHandler);
        markerEl.removeEventListener('mouseover', mouseOverHandler);
        markerEl.removeEventListener('mouseleave', mouseLeaveHandler);
      };
    });

    triggerRerenderMarkers({});
  }, [map, points]);

  const prevPositionParamsRef = useRef<MapPositionParams | null>(null);

  useEffect(() => {
    if (!map || map.isMoving()) {
      return;
    }

    const currentParams = { zoom, center, points };
    const shouldRecalculateMapPosition =
      !prevPositionParamsRef.current ||
      !isEqual(prevPositionParamsRef.current, currentParams);

    if (shouldRecalculateMapPosition) {
      setMapPosition({
        map,
        points,
        zoom,
        center,
        flyToOptions
      });
    }

    prevPositionParamsRef.current = currentParams;
  }, [map, zoom, center, points, flyToOptions]);

  useEffect(() => {
    if (map) {
      map.setStyle(getStyleURL(mapStyle, widgetToken));
    }
  }, [mapStyle]);

  const flyTo = useCallback(
    (
      to: { center?: { lng: number; lat: number }; zoom?: number },
      options?: FlyToOptions,
      eventData?: any
    ) => {
      if (map) {
        const isPointInMapViewport = isPointInBounds(
          to.center,
          map.getBounds()
        );
        map.flyTo(
          {
            ...flyToOptions,
            ...options,
            ...to,
            ...(isPointInMapViewport ? {} : { duration: 0 })
          },
          eventData
        );
      }
    },
    [map, flyToOptions]
  );

  const flyToPoints = useCallback(
    (
      coordinatesList: {
        lng: number;
        lat: number;
      }[],
      options: FlyToOptions,
      eventData?: any
    ) => {
      const points = calculateBounds(coordinatesList);
      if (map && points) {
        map.fitBounds(points, { ...flyToOptions, ...options }, eventData);
      }
    },
    [map, flyToOptions]
  );

  const showAllPoints = useCallback(
    (options?: FlyToOptions) => {
      const pointsList = points.map((point) => point.coordinates);
      const bbox = calculateBounds(pointsList);
      if (map && points && bbox) {
        map.fitBounds(bbox, { ...flyToOptions, ...options });
      }
    },
    [map, flyToOptions]
  );

  const applyClassToMarker = useCallback(
    (className: 'active' | 'hovered', id: string | undefined) => {
      const markers =
        map?._container.querySelectorAll<HTMLElement>('.maplibregl-marker');
      markers?.forEach((marker) => {
        marker.classList.remove(className);
        marker.style.zIndex = '0';
      });

      if (containerRef.current && id) {
        const marker = document.querySelector<HTMLElement>(
          `.${containerRef.current.id}_${id}`
        );
        if (marker) {
          marker.classList.add(className);
          marker.style.zIndex = '1';
        }
      }
    },
    [map]
  );

  const setActiveMarker = useCallback(
    (id: string | undefined) => applyClassToMarker('active', id),
    [applyClassToMarker]
  );

  const setHoveredMarker = useCallback(
    (id: string | undefined) => applyClassToMarker('hovered', id),
    [applyClassToMarker]
  );

  const onMarkerClick = (callback: MarkerCallback, type: string) => {
    onMarkerClickCallbacksRef.current = {
      ...onMarkerClickCallbacksRef.current,
      [type]: callback
    };

    return () => {
      const removedCallback: Record<string, MarkerCallback> = {};
      Object.entries(onMarkerClickCallbacksRef.current || {})
        .filter(([cbType]) => type !== cbType)
        .forEach(([k, v]) => (removedCallback[k] = v));

      onMarkerClickCallbacksRef.current = removedCallback;
    };
  };

  const onMarkerHover = (callback: MarkerCallback, type: string) => {
    onMarkerHoverCallbacksRef.current = {
      ...onMarkerHoverCallbacksRef.current,
      [type]: callback
    };

    return () => {
      const removedCallback: Record<string, MarkerCallback> = {};
      Object.entries(onMarkerHoverCallbacksRef.current || {})
        .filter(([cbType]) => type !== cbType)
        .forEach(([k, v]) => (removedCallback[k] = v));

      onMarkerHoverCallbacksRef.current = removedCallback;
    };
  };

  useImperativeHandle(
    forwardingAPIRef,
    () => ({
      map,
      flyTo,
      flyToPoints,
      showAllPoints,
      setActiveMarker,
      setHoveredMarker,
      onMarkerHover,
      onMarkerClick
    }),
    [map, flyTo, showAllPoints, flyToPoints, setActiveMarker, setHoveredMarker]
  );

  const renderMarkers = useMemo(
    () =>
      pointsGeoJson.features.map((point) => {
        if (!containerRef.current) return;

        const markerUniqueId = `${containerRef.current.id}_${point.properties.id}`;
        const markerElement = map?._container.querySelector(
          `[class*='${markerUniqueId}']`
        );

        if (map && markerElement) {
          const isUserLocation = point.properties.id === 'user_location';
          const MarkerIcon = !isUserLocation ? (
            <Marker
              key={markerUniqueId}
              color={markerColor}
              iconUrl={markerIconUrl}
            />
          ) : (
            <UserLocation key={markerUniqueId} color={markerColor} />
          );

          return createPortal(MarkerIcon, markerElement);
        }

        return null;
      }),
    [rerenderMarkers, markerColor, markerIconUrl]
  );

  return (
    <>
      <MapSizeWrapper _width={width} _height={height} {...forwardingProps}>
        <MapContainer ref={containerRef} id={containerId} />
      </MapSizeWrapper>

      {renderMarkers}
    </>
  );
});

const MapContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;

  .maplibregl-ctrl-bottom-right {
    opacity: 0.3;
    font-size: 10px;
  }
`;

const MapSizeWrapper = styled.div<{
  _width: string;
  _height: string;
}>`
  width: ${({ _width }) => _width};
  height: ${({ _height }) => _height};
`;
